import React from "react";
import { useQuery } from "@apollo/client";

import * as UsageQL from "queries/usage";
import { LimitsPanel } from "components/statistics/limits/panel";
import { formatBytes } from "components/statistics/limits/format";

// A BUCKET's size against its limits.
//
// The query lives here rather than in the panel because the scope is the whole
// point: this read is composed server-side from the resolved bucket row and
// carries the {orgId, bucketId} value id. The organization's version of this
// screen reads a different field, guarded by a different pair of assertions,
// and draws the same panel — see routes/dashboard/limits.js.

const read = (data) =>
  data?.viewer?.account?.buckets?.get?.usage?.list || null;

// The per-file bounds. Configuration rather than consumption: they are not
// measured against anything, so they get plain text and never a bar. There is
// no organization-wide equivalent — a bound belongs to the bucket that declares
// it.
const FileBounds = ({ minFileSize, maxFileSize }) => (
  <div className="d-flex justify-content-between">
    <small className="text-muted">Per file</small>
    <small>
      {formatBytes(minFileSize)} min · {formatBytes(maxFileSize)} max
    </small>
  </div>
);

export const Limits = ({ bucketId, values = {}, compact = false }) => {
  const { data, loading, error } = useQuery(UsageQL.list, {
    variables: { bucketId },
    notifyOnNetworkStatusChange: true,
  });

  return (
    <LimitsPanel
      listing={read(data)}
      loading={loading}
      error={error}
      errorMessage={"Could not read this bucket's limits."}
      empty={"No limits are configured for this bucket."}
      truncatedMessage={
        "Partial: this bucket has more limits than one page holds, so some are not shown."
      }
    >
      {compact ? null : (
        <FileBounds
          minFileSize={values.minFileSize}
          maxFileSize={values.maxFileSize}
        />
      )}
    </LimitsPanel>
  );
};

// The bucket page's version: the bars and nothing else — no per-file bounds,
// which are already in the fields block above it there.
export const LimitsSummary = ({ bucketId }) => (
  <Limits bucketId={bucketId} compact />
);

export default Limits;
